<template>
  <div class="proformas-page">
    <!-- Header -->
    <div class="prov-header">
      <div>
        <span class="header-eyebrow">B2B</span>
        <h2 class="header-title">Control Pagos</h2>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="kpi-strip">
      <div class="kpi-card kpi-card--alerta">
        <span class="kpi-label">Por pagar</span>
        <span class="kpi-value">{{ noPagadas.length }}</span>
        <span class="kpi-foot">proformas</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Importe pendiente</span>
        <span class="kpi-value kpi-value--sm">{{ importePartes(false) || '--' }}</span>
        <span class="kpi-foot">sin cobrar (due + restos)</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Pagadas</span>
        <span class="kpi-value">{{ pagadas.length }}</span>
        <span class="kpi-foot">proformas completas</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Importe cobrado</span>
        <span class="kpi-value kpi-value--sm">{{ importePartes(true) || '--' }}</span>
        <span class="kpi-foot">cobrado</span>
      </div>
    </div>

    <!-- Listado -->
    <div class="list-card">
      <div class="list-card__header">
        <div class="list-card__title-row">
          <h3 class="list-card__title">Listado</h3>
          <div class="leyenda">
            <span class="leyenda__item"><span class="leyenda__dot leyenda__dot--pend"></span> Pendiente</span>
            <span class="leyenda__item"><span class="leyenda__dot leyenda__dot--soon"></span> Vence pronto</span>
            <span class="leyenda__item"><span class="leyenda__dot leyenda__dot--over"></span> Vencida</span>
          </div>
        </div>
        <div class="list-card__filters">
          <div class="filter-pills">
            <button
              v-for="f in ['No pagadas', 'Vencidas', 'Pagadas', 'Todas']"
              :key="f"
              class="pill"
              :class="{ active: estadoFiltro === f, 'pill--vencidas': f === 'Vencidas' && vencidas.length }"
              @click="estadoFiltro = f"
            >{{ f }}<template v-if="f === 'Vencidas' && vencidas.length"> ({{ vencidas.length }})</template></button>
          </div>
          <div class="search-box">
            <i class="fa-solid fa-magnifying-glass search-box__icon"></i>
            <input
              type="text"
              class="search-box__input"
              placeholder="Buscar por cliente, proforma o pedido..."
              v-model="buscar"
            />
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <b-table
          :items="proformasFiltradas"
          :fields="columnas"
          class="mb-0"
          hover
          responsive
          show-empty
          :empty-text="cargando ? 'Cargando...' : 'No hay proformas'"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(toggle)="data">
            <button
              class="action-btn"
              :title="data.detailsShowing ? 'Ocultar detalle' : 'Ver documento y pedido'"
              @click="abrirDetalle(data)"
            >
              <i class="fa-solid" :class="data.detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </button>
          </template>

          <template #cell(numero)="data">
            <span class="cell-numero-wrap">
              <span class="cell-numero">{{ data.item.numero }}</span>
              <span class="badge-pct badge-pct--fac" v-if="data.item.esFactura">FACTURA</span>
              <span class="badge-pct badge-pct--dep" v-else-if="data.item.tipo === 'deposito'">DEP {{ Number(data.item.proformaPct) }}%</span>
              <span class="badge-pct" v-else-if="Number(data.item.proformaPct) !== 100">{{ Number(data.item.proformaPct) }}%</span>
            </span>
          </template>

          <template #cell(pedidoId)="data">
            <span class="cell-mono">{{ data.item.pedidoRef || `#${data.item.pedidoId}` }}</span>
          </template>

          <template #cell(fecha)="data">
            <span class="cell-mono">{{ formatFecha(data.item.fecha) }}</span>
          </template>

          <template #cell(expiry)="data">
            <span class="cell-mono">{{ formatFecha(fechaDue(data.item)) || '—' }}</span>
          </template>

          <template #cell(pagado)="data">
            <span class="cell-mono">{{ data.item.simbolo }}{{ importePagado(data.item).toFixed(2) }}</span>
          </template>

          <template #cell(balance)="data">
            <span class="cell-mono cell-total">{{ data.item.simbolo }}{{ balance(data.item).toFixed(2) }}</span>
          </template>

          <template #cell(cliente)="data">
            <span
              v-if="data.item.customerId"
              class="cell-cliente cell-cliente--link"
              @click="$router.push({ name: 'customer-profile', params: { id: data.item.customerId } })"
            >{{ nombreCliente(data.item) }}</span>
            <span v-else class="cell-cliente">{{ nombreCliente(data.item) }}</span>
          </template>

          <template #cell(totalDue)="data">
            <span class="cell-mono cell-total">{{ data.item.simbolo }}{{ importeTotal(data.item).toFixed(2) }}</span>
          </template>

          <template #cell(pagada)="data">
            <span class="badge-status" :class="`badge-status--${estadoInfo(data.item).clase}`">
              {{ estadoInfo(data.item).texto }}
            </span>
          </template>

          <template #cell(dias)="data">
            <span
              v-if="estadoInfo(data.item).dias !== null"
              class="cell-dias"
              :class="`cell-dias--${estadoInfo(data.item).clase}`"
            >{{ estadoInfo(data.item).dias }}</span>
            <span v-else class="cell-mono text-muted">—</span>
          </template>

          <template #cell(acciones)="data">
            <button
              class="action-btn action-btn--pdf"
              title="Ver PDF"
              :disabled="pdfId === data.item.id"
              @click="data.item.esFactura ? verPdfFactura(data.item) : verPdf(data.item)"
            >
              <b-spinner v-if="pdfId === data.item.id" small />
              <i v-else class="fa-solid fa-file-pdf"></i>
            </button>
          </template>

          <!-- Detalle: proforma o factura + pedido origen -->
          <template #row-details="data">
            <div class="detalle">
              <!-- Factura -->
              <div v-if="data.item.esFactura" class="detalle__panel">
                <div class="detalle__title"><i class="fa-solid fa-file-invoice-dollar mr-25"></i> Factura {{ data.item.numero }}</div>
                <div class="detalle__cobros">
                  <span class="badge-estado badge-estado--no">
                    <i class="fa-solid fa-circle-minus"></i>
                    Due {{ data.item.simbolo }}{{ fmtImporte(data.item.balanceDue) }}
                    <template v-if="data.item.vencimiento"> · vence {{ formatFecha(data.item.vencimiento) }}</template>
                  </span>
                </div>
                <div class="detalle__items">
                  <div v-for="(it, i) in data.item.items" :key="i" class="detalle__row">
                    <div class="min-w-0">
                      <span class="detalle__name">{{ it.title || it.sku }}</span>
                      <small class="text-muted d-block">{{ it.sku }}</small>
                    </div>
                    <span class="cell-mono">x{{ it.qty }}</span>
                    <span class="cell-mono">{{ data.item.simbolo }}{{ Number(it.unitPrice).toFixed(2) }}</span>
                    <span class="cell-mono cell-total">{{ data.item.simbolo }}{{ (it.qty * it.unitPrice).toFixed(2) }}</span>
                  </div>
                </div>
                <div class="detalle__totales">
                  <span>Subtotal <b>{{ data.item.simbolo }}{{ Number(data.item.subtotal).toFixed(2) }}</b></span>
                  <span v-if="Number(data.item.shipping) > 0">Shipping <b>{{ data.item.simbolo }}{{ Number(data.item.shipping).toFixed(2) }}</b></span>
                  <span v-if="Number(data.item.vatImporte) > 0">VAT ({{ Number(data.item.vatPct) }}%) <b>{{ data.item.simbolo }}{{ Number(data.item.vatImporte).toFixed(2) }}</b></span>
                  <span>Total <b>{{ data.item.simbolo }}{{ Number(data.item.total).toFixed(2) }}</b></span>
                  <span v-if="Number(data.item.pagosRecibidos) > 0">Pagos recibidos <b>{{ data.item.simbolo }}{{ Number(data.item.pagosRecibidos).toFixed(2) }}</b></span>
                </div>
              </div>

              <!-- Proforma -->
              <div v-else class="detalle__panel">
                <div class="detalle__title"><i class="fa-solid fa-file-invoice mr-25"></i> Proforma {{ data.item.numero }}</div>

                <!-- Cobros: marcar pagado el due y el resto -->
                <div class="detalle__cobros">
                  <span
                    class="badge-estado"
                    :class="data.item.pagada ? 'badge-estado--ok' : 'badge-estado--no'"
                    :title="data.item.pagada ? 'Marcar como no pagado' : 'Marcar como pagado (se anota en el historial de pagos)'"
                    @click="togglePagada(data.item, 'due')"
                  >
                    <i class="fa-solid" :class="data.item.pagada ? 'fa-circle-check' : 'fa-circle-minus'"></i>
                    Due {{ data.item.simbolo }}{{ fmtImporte(data.item.totalDue) }}
                  </span>
                  <span
                    v-if="Number(data.item.restante) > 0"
                    class="badge-estado"
                    :class="data.item.pagadaRestante ? 'badge-estado--ok' : (restanteVencido(data.item) ? 'badge-estado--danger' : 'badge-estado--no')"
                    :title="restanteTitle(data.item)"
                    @click="togglePagada(data.item, 'restante')"
                  >
                    <i class="fa-solid" :class="data.item.pagadaRestante ? 'fa-circle-check' : (restanteVencido(data.item) ? 'fa-triangle-exclamation' : 'fa-circle-minus')"></i>
                    Resto {{ data.item.simbolo }}{{ fmtImporte(data.item.restante) }}
                    <template v-if="!data.item.pagadaRestante && fechaResto(data.item)"> · vence {{ formatFecha(fechaResto(data.item)) }}</template>
                  </span>
                </div>
                <div class="detalle__items">
                  <div v-for="(it, i) in data.item.items" :key="i" class="detalle__row">
                    <div class="min-w-0">
                      <span class="detalle__name">{{ it.title || it.sku }}</span>
                      <small class="text-muted d-block">{{ it.sku }}</small>
                    </div>
                    <span class="cell-mono">x{{ it.qty }}</span>
                    <span class="cell-mono">{{ data.item.simbolo }}{{ Number(it.unitPrice).toFixed(2) }}</span>
                    <span class="cell-mono cell-total">{{ data.item.simbolo }}{{ (it.qty * it.unitPrice).toFixed(2) }}</span>
                  </div>
                </div>
                <div class="detalle__totales">
                  <span>Order value <b>{{ data.item.simbolo }}{{ Number(data.item.orderValue).toFixed(2) }}</b></span>
                  <span v-if="Number(data.item.proformaPct) !== 100">Proforma <b>{{ Number(data.item.proformaPct) }}%</b></span>
                  <span>Total due <b>{{ data.item.simbolo }}{{ Number(data.item.totalDue).toFixed(2) }}</b></span>
                  <span v-if="Number(data.item.restante) > 0">
                    Resto <b>{{ data.item.simbolo }}{{ Number(data.item.restante).toFixed(2) }}</b>
                    <template v-if="data.item.restanteFecha"> (vence {{ formatFecha(data.item.restanteFecha) }})</template>
                  </span>
                </div>
              </div>

              <!-- Pedido origen -->
              <div class="detalle__panel">
                <div class="detalle__title"><i class="fa-solid fa-clipboard-list mr-25"></i> Pedido {{ data.item.pedidoRef || `#${data.item.pedidoId}` }}</div>
                <div v-if="!pedidosCache[data.item.pedidoId]" class="text-center text-muted my-1">
                  <b-spinner small class="align-middle mr-50" /> Cargando pedido...
                </div>
                <template v-else>
                  <div class="detalle__meta">
                    <span>Fecha <b>{{ formatFecha(pedidosCache[data.item.pedidoId].createdAt) }}</b></span>
                    <span>Total <b>{{ pedidosCache[data.item.pedidoId].simbolo }}{{ pedidosCache[data.item.pedidoId].total }}</b></span>
                    <span v-if="Number(pedidosCache[data.item.pedidoId].descuentoPct)">Descuento <b>{{ pedidosCache[data.item.pedidoId].descuentoPct }}%</b></span>
                    <span>Email <b>{{ pedidosCache[data.item.pedidoId].cliente.email || '--' }}</b></span>
                  </div>
                  <div class="detalle__items">
                    <div v-for="(it, i) in pedidosCache[data.item.pedidoId].items" :key="i" class="detalle__row">
                      <div class="min-w-0">
                        <span class="detalle__name">{{ it.title }}</span>
                        <small class="text-muted d-block">{{ it.sku }}</small>
                      </div>
                      <span class="cell-mono">x{{ it.qty }}</span>
                      <span class="cell-mono">{{ pedidosCache[data.item.pedidoId].simbolo }}{{ it.wspUnit }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>
        </b-table>
      </div>

      <div class="list-card__footer list-card__footer--totales">
        <span class="footer-count">{{ proformasFiltradas.length }} documentos</span>
        <span class="footer-totales">
          Total <b>{{ sumaFiltradas(p => importeTotal(p)) || '--' }}</b>
          · Pagado <b class="text-success">{{ sumaFiltradas(p => importePagado(p)) || '--' }}</b>
          · Balance <b class="text-danger">{{ sumaFiltradas(p => balance(p)) || '--' }}</b>
        </span>
      </div>
    </div>

  </div>
</template>

<script>
import { BTable, BSpinner } from 'bootstrap-vue'
import axios from '@axios'

export default {
  components: { BTable, BSpinner },
  data() {
    return {
      proformas: [],
      facturas: [],
      cargando: true,
      buscar: '',
      estadoFiltro: 'No pagadas',
      pdfId: null,
      pedidosCache: {},
      columnas: [
        { key: 'toggle', label: '', thStyle: { width: '36px' } },
        { key: 'numero', label: 'Nº', sortable: true },
        { key: 'pedidoId', label: 'Pedido', sortable: true },
        { key: 'fecha', label: 'Fecha', sortable: true },
        { key: 'expiry', label: 'Vencimiento' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'totalDue', label: 'Total', sortable: true },
        { key: 'pagado', label: 'Pagado' },
        { key: 'balance', label: 'Balance' },
        { key: 'pagada', label: 'Estado' },
        { key: 'dias', label: 'Días' },
        { key: 'acciones', label: '', thStyle: { width: '60px' } },
      ],
    }
  },
  computed: {
    // Proformas + facturas sin pagar, en un único listado
    documentos() {
      return [...this.proformas, ...this.facturas.map(f => ({ ...f, esFactura: true }))]
    },
    // Pagada del todo: el due y (si lo hay) el restante
    pagadas() {
      return this.documentos.filter(p => this.pagadaCompleta(p))
    },
    noPagadas() {
      return this.documentos.filter(p => !this.pagadaCompleta(p))
    },
    vencidas() {
      return this.documentos.filter(p => this.estaVencida(p))
    },
    proformasFiltradas() {
      let lista = this.documentos
      if (this.estadoFiltro === 'Pagadas') lista = this.pagadas
      if (this.estadoFiltro === 'No pagadas') lista = this.noPagadas
      if (this.estadoFiltro === 'Vencidas') lista = this.vencidas

      const q = this.buscar.toLowerCase().trim()
      if (!q) return lista
      return lista.filter(p => {
        const campos = [p.numero, this.nombreCliente(p), p.clienteEmail, p.pedidoRef, `#${p.pedidoId}`]
        return campos.some(c => c && c.toString().toLowerCase().includes(q))
      })
    },
  },
  methods: {
    nombreCliente(p) {
      return p.clienteEmpresa || [p.clienteNombre, p.clienteApellidos].filter(Boolean).join(' ') || '—'
    },
    formatFecha(f) {
      if (!f) return '--'
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleDateString('es-ES')
    },
    pagadaCompleta(p) {
      if (p.esFactura) return !!p.pagada
      return !!p.pagada && (Number(p.restante) <= 0 || !!p.pagadaRestante)
    },
    fmtImporte(n) {
      const v = Math.round((Number(n) || 0) * 100) / 100
      return Number.isInteger(v) ? String(v) : v.toFixed(2)
    },
    // Suma un importe sobre las proformas filtradas, agrupado por moneda
    sumaFiltradas(fn) {
      const porMoneda = {}
      this.proformasFiltradas.forEach(p => {
        const s = p.simbolo || '£'
        porMoneda[s] = (porMoneda[s] || 0) + (Number(fn(p)) || 0)
      })
      return Object.keys(porMoneda)
        .map(s => `${s}${(Math.round(porMoneda[s] * 100) / 100)}`)
        .join(' + ')
    },
    // Importe total a cobrar de la proforma (due + resto) o de la factura
    importeTotal(p) {
      if (p.esFactura) return Math.round((Number(p.total) || 0) * 100) / 100
      return Math.round(((Number(p.totalDue) || 0) + (Number(p.restante) || 0)) * 100) / 100
    },
    importePagado(p) {
      if (p.esFactura) return Math.round((Number(p.pagosRecibidos) || 0) * 100) / 100
      let imp = 0
      if (p.pagada) imp += Number(p.totalDue) || 0
      if (p.pagadaRestante) imp += Number(p.restante) || 0
      return Math.round(imp * 100) / 100
    },
    balance(p) {
      return Math.round((this.importeTotal(p) - this.importePagado(p)) * 100) / 100
    },
    // Fecha del próximo cobro pendiente (la más cercana entre vencimiento y resto);
    // el expiry solo cuenta si el due sigue sin pagar
    fechaDue(p) {
      if (this.pagadaCompleta(p)) return null
      if (p.esFactura) return p.vencimiento ? new Date(p.vencimiento) : null
      const fechas = []
      if (!p.pagada && p.expiry) fechas.push(new Date(p.expiry))
      if (Number(p.restante) > 0 && !p.pagadaRestante) {
        const fr = this.fechaResto(p)
        if (fr) fechas.push(fr)
      }
      const validas = fechas.filter(f => !Number.isNaN(f.getTime()))
      if (!validas.length) return null
      return new Date(Math.min(...validas.map(f => f.getTime())))
    },
    // Estado tipo facturación: PAGADA / PARCIAL / AL DÍA / VENCE PRONTO / VENCIDA + días al vencimiento
    estadoInfo(p) {
      if (this.pagadaCompleta(p)) return { texto: 'PAGADA', clase: 'paid', dias: null }
      // Cobrada en parte (p. ej. due pagado, resto pendiente) pero dentro de plazo
      const parcial = this.importePagado(p) > 0
      const fecha = this.fechaDue(p)
      if (!fecha) return parcial ? { texto: 'PARCIAL', clase: 'partial', dias: null } : { texto: 'PENDIENTE', clase: 'pend', dias: null }
      const dias = Math.ceil((fecha - new Date()) / 86400000)
      if (dias < 0) return { texto: 'VENCIDA', clase: 'over', dias }
      if (dias <= 7) return { texto: 'VENCE PRONTO', clase: 'soon', dias }
      return parcial ? { texto: 'PARCIAL', clase: 'partial', dias } : { texto: 'PENDIENTE', clase: 'pend', dias }
    },
    // Fecha límite del resto: la guardada o, si no existe (proformas antiguas),
    // la fecha de la proforma + los días de plazo (30 por defecto)
    fechaResto(p) {
      if (p.restanteFecha) return new Date(p.restanteFecha)
      const base = new Date(p.fecha || p.createdAt)
      if (Number.isNaN(base.getTime())) return p.expiry ? new Date(p.expiry) : null
      return new Date(base.getTime() + (parseInt(p.restanteDias, 10) || 30) * 86400000)
    },
    restanteVencido(p) {
      if (p.pagadaRestante || Number(p.restante) <= 0) return false
      const fecha = this.fechaResto(p)
      return fecha ? fecha < new Date() : false
    },
    restanteTitle(p) {
      if (p.pagadaRestante) return 'Marcar como no pagado'
      const vence = p.restanteFecha ? ` — vence ${this.formatFecha(p.restanteFecha)}` : ''
      return `Marcar como pagado (se anota en el historial de pagos)${vence}`
    },
    estaVencida(p) {
      if (this.pagadaCompleta(p)) return false
      if (p.esFactura) {
        const f = this.fechaDue(p)
        return f ? f < new Date() : false
      }
      if (this.restanteVencido(p)) return true
      if (p.pagada || !p.expiry) return false
      return new Date(p.expiry) < new Date()
    },
    // Suma por moneda de los cobros (due y resto) pagados o pendientes
    importePartes(pagados) {
      const porMoneda = {}
      this.proformas.forEach(p => {
        const s = p.simbolo || '£'
        let imp = 0
        if (!!p.pagada === pagados) imp += Number(p.totalDue) || 0
        if (Number(p.restante) > 0 && !!p.pagadaRestante === pagados) imp += Number(p.restante) || 0
        if (imp > 0) porMoneda[s] = (porMoneda[s] || 0) + imp
      })
      // Facturas sin pagar: su balance due cuenta como pendiente
      if (!pagados) {
        this.facturas.forEach(f => {
          const s = f.simbolo || '£'
          const imp = Number(f.balanceDue) || 0
          if (imp > 0) porMoneda[s] = (porMoneda[s] || 0) + imp
        })
      }
      return Object.keys(porMoneda).map(s => `${s}${(Math.round(porMoneda[s] * 100) / 100)}`).join(' + ')
    },
    abrirDetalle(row) {
      row.toggleDetails()
      const id = row.item.pedidoId
      if (!row.detailsShowing && !this.pedidosCache[id]) {
        axios
          .get(`/escaner/pedidos/${id}`)
          .then(({ data }) => { this.$set(this.pedidosCache, id, data) })
          .catch(() => {})
      }
    },
    togglePagada(pf, parte) {
      const actual = parte === 'restante' ? pf.pagadaRestante : pf.pagada
      axios
        .put(`/escaner/proformas/${pf.id}/pagada`, { pagada: !actual, parte })
        .then(({ data }) => {
          this.$set(pf, parte === 'restante' ? 'pagadaRestante' : 'pagada', data.pagada ? 1 : 0)
          const etiqueta = parte === 'restante' ? 'Resto' : 'Total due'
          this.$bvToast.toast(
            data.pagada
              ? `${pf.numero}: ${etiqueta} pagado — anotado en el historial de pagos del pedido`
              : `${pf.numero}: ${etiqueta} marcado como no pagado — pago retirado`,
            { title: 'Proformas', variant: 'success', solidColor: true },
          )
        })
        .catch(() => {
          this.$bvToast.toast('No se pudo actualizar el estado', { title: 'Error', variant: 'danger', solidColor: true })
        })
    },
    verPdf(pf) {
      this.pdfId = pf.id
      axios
        .get(`/escaner/proformas/${pf.id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => {
          this.$bvToast.toast('No se pudo abrir el PDF', { title: 'Error', variant: 'danger', solidColor: true })
        })
        .finally(() => { this.pdfId = null })
    },
    verPdfFactura(fa) {
      axios
        .get(`/escaner/facturas/${fa.id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => {
          this.$bvToast.toast('No se pudo abrir el PDF', { title: 'Error', variant: 'danger', solidColor: true })
        })
    },
    load() {
      axios.post('/uso', { seccion: 'proformas' }).catch(() => {})
      axios
        .get('/escaner/proformas')
        .then(({ data }) => { this.proformas = data || [] })
        .catch(() => {})
        .finally(() => { this.cargando = false })
      axios
        .get('/escaner/facturas')
        .then(({ data }) => { this.facturas = data || [] })
        .catch(() => {})
    },
  },
  mounted() {
    this.load()
  },
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$success: #10B981;
$danger: #EF4444;
$warning: #F59E0B;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.proformas-page {
  width: 100%;
  padding: 0 8px;
}

.prov-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 20px;
}

.header-eyebrow {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 2px;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

.kpi-strip {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.kpi-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: $shadow;

  &--alerta {
    border-color: rgba($warning, 0.5);
  }
}

.kpi-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
}

.kpi-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 22px;
  font-weight: 700;
  color: $text;
  font-feature-settings: "tnum";
  margin: 4px 0 2px;

  &--sm { font-size: 16px; margin-top: 8px; }
}

.kpi-foot {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: $text-muted;
}

.list-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;

  &__header {
    padding: 20px 22px;
    border-bottom: 1px solid $border;
  }

  &__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: $text;
    margin: 0 0 14px;
  }

  &__filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }

  &__footer {
    padding: 12px 22px;
    border-top: 1px solid $border;
  }
}

.filter-pills {
  display: flex;
  gap: 6px;
}

.pill {
  padding: 5px 14px;
  border-radius: 20px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: $primary;
    color: $primary;
  }

  &.active {
    background: $primary;
    border-color: $primary;
    color: white;
  }

  &--vencidas:not(.active) {
    border-color: rgba($danger, 0.4);
    color: $danger;
  }

  &--vencidas.active {
    background: $danger;
    border-color: $danger;
  }
}

.search-box {
  position: relative;
  width: 260px;

  &__icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: $text-muted;
    font-size: 12px;
  }

  &__input {
    width: 100%;
    padding: 7px 12px 7px 32px;
    border: 1px solid $border;
    border-radius: $radius-sm;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    color: $text;
    background: transparent;
    transition: border-color 0.15s ease;

    &::placeholder { color: #CBD5E1; }
    &:focus { outline: none; border-color: $primary; }
  }
}

// Los ::v-deep van bajo .proformas-page a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.proformas-page ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: $text-muted;
  border-top: none;
  border-bottom: 1px solid $border;
  padding: 9px 8px;
  background: transparent;
  white-space: nowrap;
}

.proformas-page ::v-deep .table-row td {
  padding: 10px 8px;
  vertical-align: middle;
  font-size: 12px;
  white-space: nowrap;
}

.cell-numero-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.cell-numero {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  color: $text;
}

.badge-pct {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 700;
  background: rgba(#7367f0, 0.1);
  color: #7367f0;
  white-space: nowrap;

  &--dep {
    background: rgba(#06B6D4, 0.1);
    color: #06B6D4;
  }

  &--fac {
    background: rgba(#10B981, 0.1);
    color: #10B981;
  }
}

.cell-mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text;
}

.cell-total {
  font-weight: 700;
}

.cell-cliente {
  font-weight: 600;
  color: $text;
  display: inline-block;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;

  &--link {
    color: #7367f0;
    cursor: pointer;
  }
}

.badge-estado {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;

  &--ok { background: rgba($success, 0.1); color: $success; }
  &--no { background: rgba($warning, 0.12); color: darken($warning, 8%); }
  &--danger { background: rgba($danger, 0.12); color: $danger; }
}

// Leyenda de estados
.list-card__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.leyenda {
  display: flex;
  gap: 16px;

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: $text-muted;
  }

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;

    &--pend { background: #b9b9c3; }
    &--soon { background: $warning; }
    &--over { background: $danger; }
  }
}

// Badge de estado tipo facturación (PAGADA / AL DÍA / VENCE PRONTO / VENCIDA)
.badge-status {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  border: 1px solid transparent;
  white-space: nowrap;

  &--paid { background: rgba($success, 0.08); color: $success; border-color: rgba($success, 0.35); }
  &--pend { background: rgba(#6e6b7b, 0.08); color: #6e6b7b; border-color: rgba(#6e6b7b, 0.3); }
  &--soon { background: rgba($warning, 0.1); color: darken($warning, 8%); border-color: rgba($warning, 0.45); }
  &--over { background: rgba($danger, 0.08); color: $danger; border-color: rgba($danger, 0.4); }
  &--partial { background: rgba($primary, 0.08); color: $primary; border-color: rgba($primary, 0.35); }
}

.cell-dias {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 700;

  &--pend { color: #6e6b7b; }
  &--soon { color: darken($warning, 8%); }
  &--over { color: $danger; }
  &--paid { color: $success; }
  &--partial { color: $primary; }
}

.detalle__cobros {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.list-card__footer--totales {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-totales {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;

  b { color: $text; }
  b.text-success { color: $success !important; }
  b.text-danger { color: $danger !important; }
}

.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.15s ease;

  &--pdf:hover { background: rgba($danger, 0.1); color: $danger; }
}

.footer-count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;
}

.min-w-0 { min-width: 0; flex: 1; }

.detalle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 4px 2px;

  &__panel {
    background: $bg;
    border: 1px solid $border;
    border-radius: $radius-sm;
    padding: 12px 14px;
  }

  &__title {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: $text-muted;
    margin-bottom: 8px;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 18px;
    font-size: 12px;
    color: $text-muted;
    margin-bottom: 8px;

    b { color: $text; }
  }

  &__items {
    display: flex;
    flex-direction: column;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 5px 0;
    border-bottom: 1px solid $border;

    &:last-child { border-bottom: none; }
  }

  &__name {
    font-size: 12px;
    font-weight: 600;
    color: $text;
    line-height: 1.2;
  }

  &__totales {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 18px;
    font-size: 12px;
    color: $text-muted;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid $border;

    b { color: $text; }
  }
}

@media (max-width: 992px) {
  .detalle { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .kpi-strip { grid-template-columns: repeat(2, 1fr); }
  .list-card__filters { flex-direction: column; align-items: flex-start; }
  .search-box { width: 100%; }
}

// Dark mode
.dark-layout {
  .proformas-page {
    .header-title { color: #E2E8F0; }

    .kpi-card { background: #1E293B; border-color: #334155; }
    .kpi-card--alerta { border-color: rgba($warning, 0.5); }
    .kpi-value { color: #F1F5F9; }

    .list-card { background: #1E293B; border-color: #334155; }
    .list-card__header { border-color: #334155; }
    .list-card__title { color: #E2E8F0; }
    .list-card__footer { border-color: #334155; }

    .pill {
      border-color: #475569;
      color: #94A3B8;
      &:hover { border-color: $primary; color: $primary; }
      &.active { background: $primary; border-color: $primary; color: white; }
    }

    .search-box__input {
      border-color: #334155;
      color: #E2E8F0;
      &::placeholder { color: #475569; }
      &:focus { border-color: $primary; }
    }

    .table-head th { color: #94A3B8; border-color: #334155; }
    .table-row td { border-color: #334155; }

    .cell-numero { color: #E2E8F0; }
    .cell-mono { color: #E2E8F0; }
    .cell-cliente { color: #E2E8F0; }
    .cell-cliente--link { color: #7367f0; }

    .footer-totales b { color: #E2E8F0; }

    .detalle__panel { background: #0F172A; border-color: #334155; }
    .detalle__name { color: #E2E8F0; }
    .detalle__meta b, .detalle__totales b { color: #E2E8F0; }
    .detalle__row { border-color: #334155; }
    .detalle__totales { border-color: #334155; }
  }
}
</style>
